import { useEffect, useState } from "react";
import { Theme, ToggleButton } from "@mui/material";
import { MRT_Row } from "material-react-table";
import { RoomlayoutServiceRow } from "../../../../../../../ServiceManagement/Roomlayout/Domain/Types/FacilityService-Roomlayout-row.types";

type RoomlayoutToggleGridProps = {
  layoutCell?: boolean[][];
  row: MRT_Row<RoomlayoutServiceRow>;
  theme: Theme;
};

const GRID_SIZE = 4;

const createDefaultRow = (): boolean[] => new Array(GRID_SIZE).fill(false);

const normalizeRow = (row: boolean[]): boolean[] => {
  return Array.from({ length: GRID_SIZE }, (_, colIndex) => row[colIndex] ?? false);
};

export const normalizeGrid = (input?: boolean[][]): boolean[][] => {
  const defaultRow = createDefaultRow();

  if (!input) {
    return Array.from({ length: GRID_SIZE }, () => [...defaultRow]);
  }

  // Normalize to GRID_SIZE x GRID_SIZE
  return Array.from({ length: GRID_SIZE }, (_, rowIndex) => {
    const row = input[rowIndex] ?? [];
    return normalizeRow(row);
  });
};

export function RoomlayoutToggleGrid({
  layoutCell,
  row,
  theme
}: Readonly<RoomlayoutToggleGridProps>) {
  const [grid, setGrid] = useState<boolean[][]>(() =>
    Array.from({ length: 4 }, () => new Array(4).fill(false))
  );

  const toggleCellInRow = (
    row: boolean[],
    rowIndex: number,
    targetRow: number,
    targetCol: number
  ): boolean[] => {
    return row.map((cell, colIndex) =>
      rowIndex === targetRow && colIndex === targetCol ? !cell : cell
    );
  };

  const toggleCell = (r: number, c: number) => {
    setGrid(prev => {
      const res = prev.map((gridRow, rowIndex) => toggleCellInRow(gridRow, rowIndex, r, c));
      row.original.layout = res;
      return res;
    });
  };

  useEffect(() => {
    if (layoutCell && layoutCell.length > 0) {
      setGrid(normalizeGrid(layoutCell));
    }
  }, [layoutCell]);

  const renderCell = (cell: boolean, rowIndex: number, colIndex: number) => (
    <ToggleButton
      key={`${rowIndex}-${colIndex}`}
      value={`${rowIndex}-${colIndex}`}
      selected={cell}
      onChange={() => toggleCell(rowIndex, colIndex)}
      sx={{
        width: 50,
        height: 50,
        borderRadius: 2,
        "&.Mui-selected": { backgroundColor: theme.palette.primary.main },
        "&.Mui-selected:hover": { backgroundColor: theme.palette.primary.main }
      }}
    />
  );

  const renderRow = (gridRow: boolean[], rowIndex: number) => (
    <div key={`${gridRow}-${rowIndex}`} style={{ display: "flex", gap: 12, marginBottom: 12 }}>
      {gridRow.map((cell, colIndex) => renderCell(cell, rowIndex, colIndex))}
    </div>
  );

  return (
    <div style={{ display: "inline-block" }}>
      {grid.map((gridRow, rowIndex) => renderRow(gridRow, rowIndex))}
    </div>
  );
}
